import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { ContentIcon } from '@/components/atoms';
import { type ServiceFact } from '@/content/services';

/**
 * The service's facts at a glance, as a definition list.
 *
 * `dl`/`dt`/`dd` and not a table or a stack of rows: each entry is a term and
 * its definition, which is exactly what the element means, and it is what
 * makes the pairing survive being read aloud — a screen reader announces "to
 * start, 30% or 50% of the CIF price" rather than two unrelated fragments.
 *
 * The grid is on the `dl` with the pairs as its children, so every value lines
 * up on one column however long the label beside it runs.
 */
export function ServiceFactList({ facts }: { facts: readonly ServiceFact[] }) {
  return (
    <Box component="dl" sx={{ m: 0, display: 'grid', gap: 0 }}>
      {facts.map((fact, index) => (
        <Box
          key={fact.label}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 3,
            rowGap: 1,
            py: 4,
            ...(index > 0 && { borderTop: 1, borderColor: 'divider' }),
          }}
        >
          <ContentIcon
            name={fact.icon}
            sx={{
              gridRow: '1 / 3',
              alignSelf: 'start',
              mt: 0.25,
              fontSize: 20,
              color: 'primary.main',
            }}
          />
          <Typography
            component="dt"
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              fontWeight: 700,
              // Was `text.disabled`, which measured 2.9:1 at 12px — a fail
              // that predates this file's rework and is fixed with it.
              color: 'text.secondary',
            }}
          >
            {fact.label}
          </Typography>
          <Typography component="dd" variant="subtitle2" sx={{ m: 0, fontWeight: 600 }}>
            {fact.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
